/**
 * Session cookie helpers.
 *
 * The portal relies on aue-mcp's signed `auth_token` cookie (ADR-0004).
 * We expose a small client-side cache of the user record for the profile
 * page, and clear it on logout.
 */

import { useEffect, useState } from 'react';
import { api } from './api';

const SESSION_STORAGE_KEY = 'aue-portal:user';

export interface PortalSession {
  id: string;
  email: string;
  displayName: string | null;
  role: string;
  apiKeyMasked: string;
  apiKey?: string;
}

export function readStoredSession(): PortalSession | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PortalSession;
  } catch {
    return null;
  }
}

export function writeStoredSession(session: PortalSession | null): void {
  if (typeof window === 'undefined') return;
  if (session === null) {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function usePortalSession(): {
  session: PortalSession | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
} {
  const [session, setSession] = useState<PortalSession | null>(() =>
    readStoredSession(),
  );
  const [loading, setLoading] = useState(false);

  const refresh = async (): Promise<void> => {
    setLoading(true);
    try {
      const me = await api.me();
      const next: PortalSession = {
        id: me.id,
        email: me.email,
        displayName: me.displayName,
        role: me.role ?? 'agent',
        apiKeyMasked: me.api_key_masked,
        apiKey: session?.apiKey,
      };
      setSession(next);
      writeStoredSession(next);
    } catch {
      setSession(null);
      writeStoredSession(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // best-effort
    }
    setSession(null);
    writeStoredSession(null);
  };

  useEffect(() => {
    if (session && !session.apiKey) {
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { session, loading, refresh, logout };
}
